import React, { useState, useEffect } from 'react';
import './TelegramModal.css';
import { APP_CONFIG } from '../config/contract';




const TelegramModal = ({ userId, userData, incomeBreakdown, onClose, addToast }) => {
  const [telegramId, setTelegramId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLinked, setIsLinked] = useState(false);

  useEffect(() => {
    const savedId = localStorage.getItem('timetotime_telegram_id');
    if (savedId) {
      setTelegramId(savedId);
      setIsLinked(true);
    }
  }, []);

  const generateReportData = () => {
    return {
      userId: userId,
      totalIncome: parseFloat(userData?.totalIncome || 0),
      totalClaimed: parseFloat(userData?.totalClaimed || 0),
      claimableAmount: parseFloat(userData?.claimableAmount || 0),
      directReferrals: userData?.totalDirectReferrals || 0,
      directReferralIncome: parseFloat(incomeBreakdown?.directReferralIncome || 0),
      levelIncome: parseFloat(incomeBreakdown?.levelIncome || 0),
      poolIncome: parseFloat(incomeBreakdown?.poolIncome || 0),
      royaltyIncome: parseFloat(incomeBreakdown?.royaltyIncome || 0)
    };
  };

  const formatReportMessage = (report) => {
    const timestamp = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    let message = `⏱️ *Time to Time Portfolio Report*\n\n`;
    message += `👤 *User ID:* \`${report.userId}\`\n`;
    message += `📊 *Account Status:* ✅ Active\n\n`;
    message += `💰 *Total Income:* $${report.totalIncome.toFixed(2)} USDT\n`;
    message += `💎 *Available:* $${report.claimableAmount.toFixed(2)} USDT\n`;
    message += `✅ *Claimed:* $${report.totalClaimed.toFixed(2)} USDT\n`;
    message += `👥 *Direct Referrals:* ${report.directReferrals}\n\n`;
    message += `📈 *Income Breakdown:*\n`;
    message += `• Direct: $${report.directReferralIncome.toFixed(2)}\n`;
    message += `• Level: $${report.levelIncome.toFixed(2)}\n`;
    message += `• Pool: $${report.poolIncome.toFixed(2)}\n`;
    message += `• Royalty: $${report.royaltyIncome.toFixed(2)}\n\n`;
    message += `📅 *Generated:* ${timestamp}\n\n`;
    message += `---\n`;
    message += `_Time to Time - Journey Through DeFi_`;

    return message;
  };

  const sendTelegramReport = async () => {
    if (!telegramId.trim()) {
      addToast('error', 'Please enter your Telegram Chat ID');
      return;
    }

    setIsSending(true);

    try {
      const reportData = generateReportData();
      const message = formatReportMessage(reportData);

      const response = await fetch(APP_CONFIG.telegramApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: telegramId,
          message: message,
          parse_mode: 'Markdown'
        })
      });

      const data = await response.json();

      if (data.success) {
        addToast('success', 'Report sent to Telegram!');
        setIsLinked(true);
        localStorage.setItem('timetotime_telegram_id', telegramId);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      addToast('error', `Failed: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleUnlink = () => {
    localStorage.removeItem('timetotime_telegram_id');
    setTelegramId('');
    setIsLinked(false);
    addToast('success', 'Telegram unlinked');
  };

  return (
    <div className="telegram-overlay" onClick={onClose}>
      <div className="telegram-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="tg-header">
          <div className="tg-icon-wrapper">
            <div className="tg-icon-ring"></div>
            <div className="tg-gif-wrap">
              <img src="/assets/gifs/t.gif" alt="Telegram" className="tg-gif" />
            </div>
          </div>
          <h2>Telegram Notifications</h2>
          <p>Get instant alerts for all activities</p>
        </div>

        <div className="tg-steps">
          <div className="step-item">
            <span className="step-num">1</span>
            <div className="step-content">
              <span className="step-title">Start the Bot</span>
              <span className="step-desc">Search and start on Telegram:</span>
              <div className="bot-box">
                <code>{APP_CONFIG.telegramBotUsername}</code>
                <button 
                  className="copy-bot-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(APP_CONFIG.telegramBotUsername);
                    addToast('success', 'Bot username copied!');
                  }}
                >
                  📋
                </button>
              </div>
            </div>
          </div>

          <div className="step-item">
            <span className="step-num">2</span>
            <div className="step-content">
              <span className="step-title">Get Your Chat ID</span>
              <span className="step-desc">Send <code>/start</code> to receive your Chat ID</span>
            </div>
          </div>

          <div className="step-item">
            <span className="step-num">3</span>
            <div className="step-content">
              <span className="step-title">Enter Chat ID Below</span>
              <span className="step-desc">Paste to enable notifications</span>
            </div>
          </div>
        </div>

        <div className="tg-input-group">
          <label>Telegram Chat ID</label>
          <input
            type="text"
            placeholder="e.g., 123456789"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
          />
        </div>

        {isLinked && (
          <div className="linked-badge">
            <span className="link-icon">✓</span>
            <span>Telegram linked! Auto-notifications enabled.</span>
            <button className="unlink-btn" onClick={handleUnlink}>Unlink</button>
          </div>
        )}

        <button
          className="send-btn"
          onClick={sendTelegramReport}
          disabled={isSending || !telegramId.trim()}
        >
          {isSending ? (
            <>
              <span className="btn-spinner"></span>
              <span>Sending...</span>
            </>
          ) : isLinked ? (
            <>📤 Send Report Now</>
          ) : (
            <>🔗 Link & Enable Notifications</>
          )}
        </button>

        <div className="tg-features">
          <p>🔔 Auto-notifications for:</p>
          <ul>
            <li>🚀 Registration</li>
            <li>⬆️ Unit Upgrades</li>
            <li>💎 Direct Income</li>
            <li>📊 Level Income</li>
            <li>🌊 Pool Income</li>
            <li>👑 Royalty Income</li>
            <li>✅ Withdrawals</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TelegramModal;
